<template>
    <kiosk-layout formContainer>
        <template v-slot:title>
            <span class="inline-flex items-center gap-3">
                <font-awesome-icon widthAuto :icon="faTowerCell" class="text-base"></font-awesome-icon>
                <span>New Campaign</span>
            </span>
        </template>
        <template v-slot:description> The campaign is created in TCR and shared to Bandwidth immediately. </template>

        <form-wrapper
            :sections="[
                { id: 'brand', label: 'Brand' },
                { id: 'packet', label: 'Packet' },
                { id: 'screenshot', label: 'Screenshot' },
                { id: 'advanced', label: 'Advanced' },
            ]"
        >
            <vx-expandable-card id="brand">
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div data-test="brand-selectable">
                            <vx-label size="md">Brand</vx-label>
                            <vx-selectable
                                v-model="form.brand_id"
                                size="lg"
                                placeholder="Search TCR brands"
                                data-test="target-brand"
                                :reduce="(option: BrandOption) => option.value"
                                :selectable="(option: BrandOption) => option.selectable"
                                :loading="loadingBrands"
                                :options="brandOptions"
                                excludeSelected
                                @search="searchBrands"
                                :clearable="false"
                                :error="displayFirstError(validate.brand_id.$errors)"
                            ></vx-selectable>
                        </div>
                        <kiosk-tcr-qualification-summary
                            v-if="form.brand_id"
                            :brandId="form.brand_id"
                            :usecase="form.usecase"
                        />
                        <vx-alert v-if="vettingWarning" color="danger" :closeable="false" data-test="vetting-warning">
                            {{ vettingWarning }}
                        </vx-alert>
                        <div>
                            <vx-label size="md">Customer tag</vx-label>
                            <vx-input
                                :modelValue="form.customer_tag"
                                @update:modelValue="onCustomerTagInput"
                                size="lg"
                                data-test="customer-tag-input"
                                :error="displayFirstError(validate.customer_tag.$errors)"
                            ></vx-input>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <kiosk-tcr-campaign-packet-fields
                id="packet"
                v-model="form"
                :externalResults="packetExternalResults"
                :minSamples="2"
            />

            <vx-expandable-card id="screenshot">
                <template v-slot:content>
                    <vx-label size="md">Opt-in form screenshot</vx-label>
                    <p class="text-sm text-slate-500 mb-2">
                        Required unless the opt-in is verbal. Attached to the campaign as a supporting document so the
                        vetter sees the consent checkbox and disclosures.
                    </p>
                    <input
                        type="file"
                        ref="screenshotInput"
                        class="opacity-0 absolute w-1 h-1"
                        :accept="IMAGE_ACCEPT"
                        aria-hidden="true"
                        :disabled="preparing"
                        data-test="screenshot-input"
                        @change="onScreenshotChange"
                    />
                    <vx-button
                        @click="screenshotInput?.click()"
                        size="sm"
                        outline
                        color="muted"
                        data-test="choose-screenshot"
                        :disabled="preparing"
                    >
                        Choose screenshot <font-awesome-icon widthAuto :icon="faUpload" />
                    </vx-button>
                    <div v-if="screenshotError" class="text-sm text-rose-600 mt-1" data-test="screenshot-error">
                        {{ screenshotError }}
                    </div>
                    <div v-else-if="screenshot" class="text-sm text-slate-500 mt-1" data-test="screenshot-ready">
                        {{ screenshot.name }} · {{ Math.round(screenshot.size / 1024) }} KB
                    </div>
                </template>
            </vx-expandable-card>

            <kiosk-tcr-campaign-form-advanced
                id="advanced"
                v-model="form"
                :enums="enums"
                :externalResults="$externalResults"
            />

            <vx-sticky-footer>
                <vx-button href="/kiosk/ten-dlc/campaigns" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="submitting || preparing"
                >
                    Create and share to Bandwidth
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faTowerCell, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxButton,
    VxExpandableCard,
    VxInput,
    VxLabel,
    VxSelectable,
    VxStickyFooter,
} from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, required } from '@vuelidate/validators';
import { isCancel } from 'axios';
import { computed, ref, watch } from 'vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import { useOptInScreenshot } from '~/components/kiosk/kiosk-ten-dlc/composables/useOptInScreenshot';
import {
    type BrandOption,
    useTcrLiveBrandOptions,
} from '~/components/kiosk/kiosk-ten-dlc/composables/useTcrLiveBrandOptions';
import KioskTcrCampaignFormAdvanced from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignFormAdvanced.vue';
import KioskTcrCampaignPacketFields from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignPacketFields.vue';
import KioskTcrQualificationSummary from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrQualificationSummary.vue';
import { campaignDefaults } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationFormDefaults';
import { IMAGE_ACCEPT } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationImage';
import { useCallout } from '~/composables/useCallout';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useQueryParams } from '~/composables/useQueryString';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcClient, adminTenDlcRegistrationClient } from '~/services';
import type { TcrBrand } from '~/services/adminTenDlcClient.types';
import type { TcrEnums } from '~/services/adminTenDlcRegistrationClient.types';
import dayjs from '~/utils/dayjs';
import { escapeHtml } from '~/utils/html';
import { catchRequestError } from '~/utils/request';
import { displayFirstError, nestLaravelErrors, validateRules } from '~/utils/validation';

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();
const callout = useCallout();

const urlParams = useQueryParams<{ brand_id?: string }>();
const form = ref(campaignDefaults(undefined === urlParams.brand_id ? '' : String(urlParams.brand_id)));
const enums = ref<TcrEnums | null>(null);
const { screenshot, screenshotError, preparing, onScreenshotChange } = useOptInScreenshot();
const screenshotInput = ref<HTMLInputElement>();
const { brandOptions, loadingBrands, searchBrands } = useTcrLiveBrandOptions(() => form.value.brand_id);
const packetExternalResults = computed(() => nestLaravelErrors($externalResults.value));
const submitting = ref(false);
const mirroredBrand = ref<TcrBrand | null>(null);

adminTenDlcRegistrationClient
    .getEnums()
    .then((response) => {
        enums.value = response.data;
    })
    .catch((e) => {
        console.error(e);
        toasted.global.platform_error();
    });

searchBrands();

const mirroredBrandCancelKey = Symbol('kiosk-tcr-campaign-form-brand');

watch(
    () => form.value.brand_id,
    (brandId) => {
        mirroredBrand.value = null;

        if ('' === brandId) {
            return;
        }

        adminTenDlcClient
            .getTcrBrands({ brand_ids: [brandId] }, { cancelKey: mirroredBrandCancelKey })
            .then((response) => {
                mirroredBrand.value = response.data.data[0] ?? null;
            })
            .catch((e) => {
                if (isCancel(e)) {
                    return;
                }

                toasted.error(catchRequestError(e), { sticky: true });
            });
    },
    { immediate: true }
);

// The mirror keeps a score only for an active vet.
const vettingWarning = computed(() => {
    const brand = mirroredBrand.value;

    if (null === brand || null !== brand.vetting_score) {
        return null;
    }

    return 'pending' === brand.vetting_status || 'unscored' === brand.vetting_status
        ? "This brand's vet hasn't scored yet; once it does, refresh this campaign's carrier terms."
        : "This brand has no vetting score; if a vet raises its terms later, refresh this campaign's carrier terms.";
});

const onCustomerTagInput = (value: string | number) => {
    form.value.customer_tag = String(value).toLowerCase();
};

// The parent validates only the two fields it renders itself; the sections own theirs.
const validate = useVuelidate(
    {
        brand_id: { required },
        customer_tag: {
            maxLength: maxLength(50),
            tag: helpers.withMessage('Lowercase letters and digits only', helpers.regex(/^[a-z0-9]*$/)),
        },
    },
    form,
    { $externalResults }
);

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    validate.value.$reset();

    const valid = await validateRules(validate);

    if (!valid || preparing.value || screenshotError.value) {
        submitting.value = false;
        return;
    }

    try {
        const response = await adminTenDlcRegistrationClient.createCampaign(form.value, screenshot.value);

        if (response.data.sharing_error) {
            toasted.error(
                `Campaign ${response.data.campaign_id} was created. ${response.data.sharing_error} Re-share it from the campaign page.`,
                { sticky: true }
            );
        } else if (response.data.screenshot_error) {
            toasted.error(
                `Campaign ${response.data.campaign_id} was created and shared. ${response.data.screenshot_error} Attach it from the campaign page.`,
                { sticky: true }
            );
        } else {
            callout.success(
                'kiosk-tcr-campaign',
                `<strong>${escapeHtml(response.data.campaign_id)}</strong> ${
                    response.data.supporting_document
                        ? 'created, shared to Bandwidth and screenshot attached'
                        : 'created and shared to Bandwidth'
                }`,
                { ttl: dayjs().add(30, 'seconds'), shadow: true }
            );
        }

        window.location.assign(`/kiosk/ten-dlc/campaigns/${response.data.campaign_id}`);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
